import { FormGroup } from '@angular/forms';
import { slicesDiffer } from './state-slice-diff.util';

/**
 * Cross-tab adoption: repaint a sheet's FormGroup from state a sibling tab
 * already persisted and this tab already rehydrated.
 *
 * THE GOVERNING INVARIANT — adoption NEVER writes back to state. It is a pure
 * render of state the tab already holds. Every write here uses
 * `{ emitEvent: false }` so the sheet's `buildFormEditPipeline` valueChanges
 * subscription does not fire: no `updateSheetData`, no persistence write, no
 * re-broadcast. Break this and you rebuild the stale-form clobber loop this
 * whole seam exists to kill.
 *
 * DI-free so it is unit-testable without TestBed.
 *
 * `FilingFormStateService.getSheetData()` — the source of `incoming` at every
 * call site — returns the live state slice BY REFERENCE, not a clone. Safe
 * today because every value this util patches into a control is a scalar or
 * string, so `slicesDiffer`'s comparison and `control.setValue()` never
 * mutate the shared object. Worth knowing if any sheet ever stores an object
 * (or array) value in its state slice — patching a control with a reference
 * that the state layer still owns could let a later form mutation reach back
 * into `FilingFormStateService`'s own BehaviorSubject value undetected.
 *
 * // Developer Note (TODO): `adoptRemoteState()` (the per-sheet component
 * // method that calls into this util) repaints form VALUES only — it never
 * // calls `reapplyConditionalValidation()` or `reapplyReactiveRules()`, the
 * // two hooks role0/role1 already use after an `emitEvent: false` API-hydrate
 * // patch to re-derive disabled/required/filtered-option state from the newly
 * // adopted driver values. This was evaluated and DELIBERATELY left out of
 * // the 2026-08-17 fix wave: role1's `reloadFilingInfoFromState()` docblock
 * // warns that firing those engines over a BULK patch can trip
 * // `setupDriverClearCascade` and wipe Basis, so the obvious fix risks
 * // destroying the values just adopted and needs its own design + tests.
 * // Exposure is narrowed today by a P.O. rule that blocks navigation away
 * // from Filing Information until pending changes are resubmitted, so the
 * // gap is same-sheet, both tabs on Filing Information, and self-heals on
 * // remount (the state layer — and therefore the submitted payload — stays
 * // correct throughout). The visible risk in that narrow window is a stale
 * // submit-gate rendering under the "disabled ⇒ no validations" rule (an
 * // enabled-looking control that should have just become disabled, or vice
 * // versa, until the sheet remounts).
 */

/**
 * Resolve the control name of the element that currently has focus, so a field
 * the user is typing in is never yanked out from under them. Returns null when
 * focus is outside the form. Uses `closest` because PrimeNG renders an inner
 * `<input>` inside a host element that carries the `formControlName` binding.
 */
function focusedControlName(form: FormGroup): string | null {
  if (typeof document === 'undefined')
    return null;

  const active = document.activeElement as Element | null;
  const host = active?.closest?.('[formcontrolname]') ?? null;
  const name = host?.getAttribute('formcontrolname') ?? null;

  return name && form.get(name) ? name : null;
}

/** Shared core. Returns the control names actually changed. */
function adoptSheetStateIntoForm(
  form: FormGroup,
  incoming: Record<string, unknown>,
  skip: ReadonlySet<string>
): string[] {
  const adopted: string[] = [];

  for (const [name, value] of Object.entries(incoming ?? {})) {
    if (skip.has(name))
      continue;

    const control = form.get(name);

    if (!control)
      continue;

    // `control.value` drops nothing here because we read the control directly,
    // but a disabled control still compares and patches correctly.
    if (!slicesDiffer(control.value, value))
      continue;

    control.setValue(value, { emitEvent: false });
    // Adopted values are not this user's edits — leaving them dirty would make
    // the sheet's own pristine-based gates misreport.
    control.markAsPristine();
    adopted.push(name);
  }

  return adopted;
}

/**
 * Auto-save sheets (role0/1/2/3/4/5/6/7/10/11/15-320100/16/17/20/21/22).
 * Skip-set is the focused control only.
 */
export function adoptIntoAutoSaveSheet(
  form: FormGroup,
  incoming: Record<string, unknown>
): string[] {
  const focused = focusedControlName(form);
  return adoptSheetStateIntoForm(form, incoming, new Set(focused ? [focused] : []));
}

/**
 * Inline edit-mode sheets (role8/9/12/13/14/15-410000/18/19/23/24).
 *
 * `openControlNames` comes from the sheet's own `editingRows`, because the
 * row-key convention differs per sheet (role19/23/24 key by
 * `qname|consolidation|period`, the rest by bare qname) and this helper must
 * not reimplement it. `collectEditableControlNames()` is a named method that
 * exists on role19 ALONE; the other nine sheets compute the same
 * `[...editingRows.values()].flatMap(s => Object.keys(s ?? {}))` inline at
 * their own call site rather than through a shared/named method — the
 * behaviour is identical, but do not cite `collectEditableControlNames()` as
 * the source for any sheet other than role19.
 */
export function adoptIntoInlineEditSheet(
  form: FormGroup,
  incoming: Record<string, unknown>,
  openControlNames: readonly string[]
): string[] {
  return adoptSheetStateIntoForm(form, incoming, new Set(openControlNames));
}
